import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Transaction } from '@/types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCircle2 } from 'lucide-react';

interface ReceiptTemplateProps {
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
  };
  receiptNumber: string;
  issueDate: Date;
  companyName: string;
  companyLogo?: string;
  className?: string;
}

export function ReceiptTemplate({
  transaction,
  client,
  receiptNumber,
  issueDate,
  companyName,
  companyLogo,
  className
}: ReceiptTemplateProps) {
  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Format dates
  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    return format(new Date(date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  return (
    <Card className={`max-w-2xl mx-auto print:shadow-none print:border-none ${className}`}>
      <CardContent className="p-8">
        {/* Header */}
        <div className="text-center mb-8">
          {companyLogo ? (
            <img
              src={companyLogo}
              alt={companyName}
              className="h-16 mx-auto mb-4 object-contain max-w-xs"
              style={{ maxHeight: '64px' }}
            />
          ) : (
            <div className="text-2xl font-bold mb-4 text-terracotta-700">
              {companyName}
            </div>
          )}
          <h1 className="text-3xl font-bold mb-2">RECIBO</h1>
          <p className="text-muted-foreground">Nº {receiptNumber}</p>
          <p className="text-sm text-muted-foreground">
            Emitido em: {formatDate(issueDate)}
          </p>
        </div>

        <Separator className="mb-6" />

        {/* Transaction Info */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <Badge variant="default" className="bg-green-100 text-green-800">
              PAGO
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Descrição</p>
              <p className="font-medium">{transaction.description}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Valor Recebido</p>
              <p className="text-2xl font-bold text-terracotta-600">
                {formatCurrency(transaction.amount)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Data do Recebimento</p>
              <p className="font-medium">
                {transaction.payment_date ? formatDate(transaction.payment_date) : formatDate(transaction.date)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Forma de Pagamento</p>
              <p className="font-medium">
                {transaction.payment_method || 'Não informado'}
              </p>
            </div>
          </div>

          {transaction.due_date && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-1">Data de Vencimento</p>
              <p className="font-medium">{formatDate(transaction.due_date)}</p>
            </div>
          )}
        </div>

        <Separator className="mb-6" />

        {/* Client Info */}
        {client && (
          <div className="mb-6">
            <h3 className="font-semibold mb-3">Dados do Cliente</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Nome</p>
                <p className="font-medium">{client.name}</p>
              </div>
              {client.email && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">E-mail</p>
                  <p className="font-medium">{client.email}</p>
                </div>
              )}
              {client.phone && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Telefone</p>
                  <p className="font-medium">{client.phone}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Additional Info */}
        {transaction.category && (
          <div className="mb-6">
            <p className="text-sm text-muted-foreground mb-1">Categoria</p>
            <Badge variant="secondary">{transaction.category}</Badge>
          </div>
        )}

        {transaction.notes && (
          <div className="mb-6">
            <p className="text-sm text-muted-foreground mb-1">Observações</p>
            <p className="text-sm">{transaction.notes}</p>
          </div>
        )}

        <Separator className="mb-6" />

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>Este recibo confirma o pagamento efetuado conforme descrito acima.</p>
          <p className="mt-2">
            {companyName} - {formatDate(issueDate)}
          </p>
        </div>

        {/* Signature Area */}
        <div className="mt-8 pt-4">
          <div className="border-t pt-4">
            <p className="text-center text-sm text-muted-foreground mb-4">
              Assinatura do Recebedor
            </p>
            <div className="h-16"></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}